import { EmptyCartGraphic } from '@app/components/EmptyCartGraphic';
import { CartLineItemListContainer } from '@app/components/LineItemsList';
import { Message, MessageSeverity } from '@app/components/Messages/types';
import { mapSeverity } from '@app/components/Messages/utils';
import {
  ErrorMessage,
  MonolithCartPaypalExpressContainer,
} from '@app/components/PayPalExpress';
import { MonolithCartStripeContainerLazy } from '@app/components/Stripe/MonolithCartStripeContainerLazy';
import { useCartAndDeliveryDatesDataContext } from '@app/data/clients/cartCheckoutClient';
import { refreshCart } from '@app/data/clients/cartCheckoutClient/CartAndDeliveryDatesDataContext/utils';
import { SimilarDesignItem } from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import { useScrollIntoView } from '@app/pages/check-out/hooks/useScrollIntoView';
import { DD_PDP_ERROR_METRIC_NAME } from '@app/pages/i/constants';
import { Message as MonolithMessage } from '@app/types/monolith/cart';
import {
  Box,
  Button,
  CheckDiscIcon,
  CloseIcon,
  constants,
  Divider,
  Drawer,
  Stack,
  Text,
} from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { useViewportWidth } from '@redbubble/next/hooks';
import {
  defineMessages,
  FormattedNumber,
  type IntlShape,
  useIntl,
} from '@redbubble/next/intl';
import { DeviceType, UserInfo } from '@redbubble/next/types';
import { ExperimentConfig } from '@redbubble/next-amplitude';
import { AnalyticsPageType } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import { useAmplitudeExperiment } from '@redbubble/next-provider/amplitude';
import {
  RSCheckoutLocation,
  RSPageTypeKey,
} from '@redbubble/next-provider/rudderstack';
import { useCallback, useRef, useState } from 'react';
import styles from './CartItemsTray.module.scss';
import {
  CART_TRAY_MSG_TYPE,
  type CartTrayMessageData,
  CartTrayMessageList,
} from './Messages';
import { SimilarDesigns } from './SimilarDesigns';
import { SimilarDesignsSkeleton } from './SimilarDesignsSkeleton';
import { filterAndSortMessages, pluckToSet } from './utils';

export interface CartItemsTrayProps {
  open: boolean;
  onCloseRequested: () => void;
  similarDesignsData?: SimilarDesignItem[];
  similarDesignsLoading?: boolean;
  rsPageType?: RSPageTypeKey;
}

const messages = defineMessages({
  accessibleTitle: {
    defaultMessage: 'Cart items tray',
  },
  checkout: {
    defaultMessage: 'Checkout',
  },
  paymentsInCartTrayExperimentTitle: {
    defaultMessage: 'Cart - {itemCount, plural, =1 {# item} other {# items}}',
  },
  similarDesigns: {
    defaultMessage: 'Similar designs',
  },
  subTotal: {
    defaultMessage: 'Subtotal',
  },
  title: {
    defaultMessage: "It's in the cart!",
  },
  viewCart: {
    defaultMessage: 'View cart',
  },
  calculateTaxAtCheckout: {
    defaultMessage: 'Tax will be calculated at checkout',
  },
});

const localiseError = (intl: IntlShape, message: ErrorMessage): string => {
  return typeof message === 'string' ? message : intl.formatMessage(message);
};

const mapMonolithCartMessage = (msg: MonolithMessage): CartTrayMessageData => ({
  ...msg,
  severity: mapSeverity(msg.severity),
});

const componentMessageToCartTrayMessage = (
  message: Message
): CartTrayMessageData => ({
  severity: MessageSeverity.Error,
  text: message.text,
  type: CART_TRAY_MSG_TYPE.Error,
});

const SubTotal: React.FC<{ subtotalAmount: number; currency: string }> = ({
  subtotalAmount,
  currency,
}) => {
  const intl = useIntl();

  return (
    <Stack
      direction={constants.HORIZONTAL}
      justifyContent="space-between"
      aria-label={intl.formatMessage(messages.subTotal)}
    >
      <Box>
        <Text type="display3" display="block">
          {intl.formatMessage(messages.subTotal)}
        </Text>
      </Box>

      <Text
        type="display3"
        textAlign={constants.RIGHT}
        data-testid="carttray-subtotal"
      >
        <FormattedNumber
          value={subtotalAmount}
          currency={currency}
          style="currency"
        />
      </Text>
    </Stack>
  );
};

export const CartItemsTray: React.FC<CartItemsTrayProps> = ({
  open,
  onCloseRequested,
  similarDesignsData = [],
  similarDesignsLoading = false,
  rsPageType,
}) => {
  const intl = useIntl();
  const { isOrAbove } = useViewportWidth();
  const [{ currency, isLoggedIn, email }] = useUserInfo<UserInfo>();
  const isTabletOrDesktop = isOrAbove(DeviceType.Tablet);
  const { cart, cartLoading, client, dispatch } =
    useCartAndDeliveryDatesDataContext();

  const cartItems = cart?.items;

  const showTaxSeparately = cart?.summary.show_tax_separately;
  const subtotalAmount = showTaxSeparately
    ? (cart?.summary.subtotal_ex_tax ?? 0)
    : (cart?.summary.subtotal ?? 0);

  const cartMessages = (cart?.messages || []).map(mapMonolithCartMessage);
  const orderedMessages = filterAndSortMessages({
    messages: cartMessages,
    productTypeIds: pluckToSet(cartItems, 'product_type_id'),
  });

  const messagesRef = useRef<HTMLElement>(null);

  const [lineItemUpdateErrors, setLineItemUpdateErrors] = useState<
    CartTrayMessageData[]
  >([]);

  const [paymentError, setPaymentErrors] = useState<CartTrayMessageData[]>([]);

  const allMessages = [
    ...paymentError,
    ...lineItemUpdateErrors,
    ...orderedMessages,
  ];

  const cartTrayExpressPaymentsEnrolment = useAmplitudeExperiment(
    ExperimentConfig.CartTrayExpressPayments
  );
  const cartTrayExpressPaymentsVariant =
    cartTrayExpressPaymentsEnrolment === 'variant';

  const cartTrayCTAEnrolment = useAmplitudeExperiment(
    ExperimentConfig.CartTrayCTA
  );
  const cartTrayCTAVariant = cartTrayCTAEnrolment === 'treatment';

  const isCartEmpty = (cart?.items?.length ?? 0) === 0;
  const cartHasItemsOrIsLoading = !isCartEmpty || cartLoading;

  const handleLineItemUpdateErrors = (messages: Message[]) => {
    setLineItemUpdateErrors(messages.map(componentMessageToCartTrayMessage));
  };

  const onStartPayPalExpressCheckout = () => {
    setPaymentErrors([]);
  };

  const handlePaymentError = useCallback(
    (message: ErrorMessage) => {
      setPaymentErrors([
        {
          severity: MessageSeverity.Error,
          text: localiseError(intl, message),
          type: CART_TRAY_MSG_TYPE.Error,
        },
      ]);
    },
    [intl]
  );

  const { scrollIntoView } = useScrollIntoView();
  const handleOnPayError = (message: ErrorMessage) => {
    handlePaymentError(message);
    scrollIntoView(messagesRef);
  };

  const handleOnCancel = async () => {
    setPaymentErrors([]);
    await refreshCart(client, dispatch).catch(() => {
      // do nothing - user will see previously fetched cart which will contain the tax information based on their address selected in Google/Apple pay
    });
  };

  const resetPaymentErrors = () => setPaymentErrors([]);

  // XXX(nc): in StripeContainer.tsx we set a MutationObserver to detect when the GooglePay overlay is added to the DOM and increase its z-index.
  // However, if one day GooglePay changes the data-testid attribute to something else, our code will break.
  // As a fallback, we also set pointer-events: none on the drawer when StripeContainer notifies us that the GooglePay overlay is launched.
  // This prevents users from interacting with the Cart Tray while GooglePay overlay is open.
  const drawerRef = useRef<HTMLDivElement>(null);

  const setPointer = (value: 'auto' | 'none') => {
    if (drawerRef.current) {
      drawerRef.current.style.pointerEvents = value;
    }
  };

  return (
    <Drawer
      accessibleTitle={intl.formatMessage(messages.accessibleTitle)}
      from={isTabletOrDesktop ? constants.RIGHT : constants.BOTTOM}
      size={constants.LARGE}
      fit={isTabletOrDesktop}
      open={open}
      onCloseRequested={() => {
        onCloseRequested();
        setLineItemUpdateErrors([]);
      }}
      allowClicksOutsideWhenActive
    >
      <Box
        ref={drawerRef}
        style={{ background: 'inherit' }}
        data-testid="cart-items-tray"
      >
        <Box className={styles.container}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={isCartEmpty ? 'flex-end' : 'space-between'}
            paddingBottom="s"
          >
            {!isCartEmpty &&
              // Title
              (cartTrayExpressPaymentsVariant ? (
                // Payments in cart tray experiment - Show the number of items in the cart
                <Box className={styles.title}>
                  <Text type="display3">
                    {intl.formatMessage(
                      messages.paymentsInCartTrayExperimentTitle,
                      {
                        itemCount: cart?.total_items_count ?? 0,
                      }
                    )}
                  </Text>
                </Box>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  className={styles.title}
                >
                  <CheckDiscIcon
                    size="medium"
                    color="var(--ds-color-expressive-green-300)"
                  />
                  <Text type="display2" marginLeft="xs">
                    {intl.formatMessage(messages.title)}
                  </Text>
                </Box>
              ))}

            {/* Close button */}
            <Box className={styles.close}>
              <Button
                strong
                size={constants.SMALL}
                circle
                iconBefore={<CloseIcon />}
                onClick={() => {
                  onCloseRequested();
                  setLineItemUpdateErrors([]);
                  setPaymentErrors([]);
                }}
                data-testid="close-cart-items-tray"
              />
            </Box>
          </Box>

          {allMessages.length > 0 && (
            /* Cart messages */
            <Box paddingBottom="xs" ref={messagesRef}>
              <CartTrayMessageList messages={allMessages} />
            </Box>
          )}

          {cartHasItemsOrIsLoading ? (
            <>
              {/* Updated the loading state to only show when no cart and
                cartLoading to make sure the items don't show loading skeletons on
                quantity update (same as on the Cart page) */}

              <CartLineItemListContainer
                narrow
                editable
                loading={!cart && cartLoading}
                analyticsPageType={AnalyticsPageType.ProductDetailsPage}
                setLineItemUpdateErrors={handleLineItemUpdateErrors}
              />
              {cartTrayCTAVariant && (
                <Box display="flex" justifyContent="flex-end" marginBottom="m">
                  <Button size={constants.MEDIUM} strong href="/cart">
                    {intl.formatMessage(messages.viewCart)}
                  </Button>
                </Box>
              )}
              <Divider color="var(--ds-color-border-light)" marginBottom="m" />

              <SubTotal subtotalAmount={subtotalAmount} currency={currency} />
            </>
          ) : (
            // We know for a fact the cart is empty, so show an empty cart graphic and $0 subtotal
            <>
              <Box display="flex" justifyContent="center">
                <EmptyCartGraphic />
              </Box>
              <Divider color="var(--ds-color-border-light)" marginY="m" />
              <SubTotal subtotalAmount={subtotalAmount} currency={currency} />
            </>
          )}

          <ErrorBoundary>
            {(similarDesignsLoading || similarDesignsData.length > 0) && (
              <>
                <Divider color="var(--ds-color-border-light)" marginY="m" />

                <Stack spacing="m" direction="vertical">
                  <Text type="display5">
                    {intl.formatMessage(messages.similarDesigns)}
                  </Text>

                  {similarDesignsLoading ? (
                    <SimilarDesignsSkeleton />
                  ) : (
                    <SimilarDesigns
                      similarDesigns={similarDesignsData}
                      rsPageType={rsPageType}
                    />
                  )}
                </Stack>
              </>
            )}
          </ErrorBoundary>
        </Box>

        {!isCartEmpty && (
          // Sticky at the bottom of the tray
          <Box className={styles.actions} data-testid="sticky-container">
            {cartTrayCTAVariant ? (
              <Button
                size={constants.LARGE}
                strong
                intent={constants.PRIMARY}
                fluid
                href="/check-out"
              >
                {intl.formatMessage(messages.checkout)}
              </Button>
            ) : (
              <Button
                size={constants.LARGE}
                strong
                intent={constants.PRIMARY}
                fluid
                href="/cart"
              >
                {intl.formatMessage(messages.viewCart)}
              </Button>
            )}

            {cartTrayExpressPaymentsVariant && (
              <Box className={styles.expressPayments}>
                {cart && (
                  <MonolithCartPaypalExpressContainer
                    metricName={DD_PDP_ERROR_METRIC_NAME}
                    onError={handlePaymentError}
                    onStartPayPalExpressCheckout={onStartPayPalExpressCheckout}
                    analyticsPageType={AnalyticsPageType.ProductDetailsPage}
                    onPayError={handleOnPayError}
                    onCancel={handleOnCancel}
                    selectorId="cart-tray-braintree-paypal-checkout-button"
                    shippingAddressEditable
                    shippingOptionsEditable
                    page="cart-tray"
                    email={(isLoggedIn && email) || null}
                    rsCheckoutLocation={RSCheckoutLocation.CartTray}
                  />
                )}
                <MonolithCartStripeContainerLazy
                  rsCheckoutLocation={RSCheckoutLocation.CartTray}
                  onPayError={(err) => {
                    handleOnPayError(err);
                    setPointer('auto');
                  }}
                  onCancel={() => {
                    handleOnCancel();
                    setPointer('auto');
                  }}
                  onConfirm={() => {
                    resetPaymentErrors();
                    setPointer('auto');
                  }}
                  onLaunch={() => {
                    resetPaymentErrors();
                    setPointer('none');
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
